import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  Pencil,
  Copy,
  Trash2,
  UserPlus,
  Building2,
  Users,
  Search,
  ListFilter,
} from "lucide-react";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import TabsCst from "@components/TabsCst";
import InputCst from "@components/InputCst/InputCst";
import SelectCst from "@components/InputCst/SelectCst";
import FiltersBarCst from "@components/FiltersBarCst";
import FiltersModalCst from "@components/FiltersModalCst";
import BulkActionBarCst from "@components/BulkActionBarCst";
import PaginatorCst from "@components/PaginatorCst";
import {
  openCreatePicker,
  useCanCreate,
} from "@components/CreateOptionPickerCst/host";
import {
  usePaginatedList,
  PaginatedListFilters,
} from "@hooks/usePaginatedList";
import { PageFilterField } from "@hooks/usePageFilters";
import {
  CATALOG_AGGREGATED_URL,
  CATALOG_URL_BY_TYPE,
  CatalogEntityType,
  TabKey,
  mapAggregatedType,
} from "./types";
import {
  CATALOG_EXCLUDE_FILTER_KEYS,
  CATALOG_PAGE_ID_BY_ENTITY,
} from "./catalogFiltersConfig";
import { useCatalogPageState } from "./hooks/useCatalogPageState";
import CatalogItem from "./components/CatalogItem";

const PAGE_SIZE = 20;
const QUICK_FILTER_KEYS = ["status"];

const CatalogV2: React.FC = () => {
  const { t } = useTranslation("catalog-v2");
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: corporateId = "" } = useParams();
  const canCreate = useCanCreate("catalog");

  const {
    activeTab,
    entityType,
    search,
    fieldFilters,
    sort,
    page,
    setActiveTab,
    update: updateTabFilters,
  } = useCatalogPageState();

  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(
    new Set(),
  );

  const [filtersModalOpen, setFiltersModalOpen] = useState(false);

  const hasActiveFilters = useMemo(
    () =>
      Object.entries(fieldFilters || {}).some(([key, val]) => {
        if (QUICK_FILTER_KEYS.includes(key)) return false;
        if (val === undefined || val === null || val === "") return false;
        if (Array.isArray(val) && val.length === 0) return false;
        return true;
      }),
    [fieldFilters],
  );

  const modalExcludeKeys = useMemo(
    () => [...CATALOG_EXCLUDE_FILTER_KEYS, ...QUICK_FILTER_KEYS],
    [],
  );

  const catalogFilterVisibility = useMemo(
    () => (field: PageFilterField, values: Record<string, any>) => {
      if (!field.onlyForType || field.onlyForType.length === 0) return true;
      const currentType = values.type;
      return !!currentType && field.onlyForType.includes(currentType);
    },
    [],
  );

  const listFilters: PaginatedListFilters = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      search: search || undefined,
      fieldFilters: { ...fieldFilters, getCounters: true },
      sort,
    }),
    [page, search, fieldFilters, sort],
  );

  const listUrl = useMemo(() => {
    if (!corporateId) return null;
    const template =
      activeTab === "all"
        ? CATALOG_AGGREGATED_URL
        : CATALOG_URL_BY_TYPE[entityType];
    return template.replace(":idCorporate", corporateId);
  }, [activeTab, entityType, corporateId]);

  const { items, totalItems, totalPages, isLoading, mutate } = usePaginatedList(
    listUrl,
    listFilters,
  );

  const normalizedItems = useMemo<
    { entity: any; entityType: CatalogEntityType }[]
  >(
    () =>
      (items || []).map((raw: any) =>
        activeTab === "all"
          ? { entity: raw.lo, entityType: mapAggregatedType(raw.type) }
          : { entity: raw, entityType },
      ),
    [items, activeTab, entityType],
  );

  const segmentForType = (type: CatalogEntityType): string =>
    type === "course" ? "courses" : type === "path" ? "paths" : "activities";

  const handleTabChange = (next: string) => {
    setActiveTab(next as TabKey);
    setSelectedIds(new Set());
  };

  const handleSelectToggle = (entityId: string | number, next: boolean) => {
    setSelectedIds((prev) => {
      const copy = new Set(prev);
      if (next) copy.add(entityId);
      else copy.delete(entityId);
      return copy;
    });
  };

  const handleToggleAll = (next: boolean) => {
    if (next) {
      setSelectedIds(new Set(normalizedItems.map((item) => item.entity.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const allChecked =
    normalizedItems.length > 0 && selectedIds.size === normalizedItems.length;
  const someChecked = selectedIds.size > 0 && !allChecked;

  const tabs = [
    { key: "all", label: t("tabs.all") },
    { key: "activities", label: t("tabs.activities") },
    { key: "courses", label: t("tabs.courses") },
    { key: "paths", label: t("tabs.paths") },
  ];

  const sortOptions = [
    { label: t("sort.most-recent"), value: "most-recent" },
    { label: t("sort.least-recent"), value: "least-recent" },
    { label: t("sort.alphabetical"), value: "alphabetical" },
  ];

  return (
    <AnimationFadeIn>
      <Box sx={{ py: 3 }}>
        <Breadcrumb />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: "28px",
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            {t("title")}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Pencil size={16} />}
            onClick={() => openCreatePicker("catalog")}
            disabled={!canCreate}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 700,
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            {t("create-button")}
          </Button>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TabsCst
            tabs={tabs}
            activeKey={activeTab}
            onChange={handleTabChange}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              width: 260,
              "& .MuiFormControl-root": {
                minWidth: "0 !important",
                width: "100% !important",
              },
              "& .MuiOutlinedInput-root": { borderRadius: "8px" },
              "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
            }}
          >
            <InputCst
              id="catalog-search"
              label={t("search-placeholder") || ""}
              value={search ?? ""}
              onChange={(e: any) =>
                updateTabFilters({ search: e.target.value, page: 1 })
              }
              endAdornment={
                <Search size={16} color={theme.palette.text.tertiary} />
              }
            />
          </Box>

          <Box
            sx={{
              width: 220,
              "&:empty": { display: "none" },
              "& .MuiGrid-container": { margin: 0, width: "100%" },
              "& .MuiGrid-item": {
                paddingTop: "0 !important",
                paddingLeft: "0 !important",
                maxWidth: "100% !important",
                flexBasis: "100% !important",
              },
              "& .MuiFormControl-root": {
                minWidth: "0 !important",
                width: "100% !important",
              },
            }}
          >
            <FiltersBarCst
              pageId={CATALOG_PAGE_ID_BY_ENTITY[entityType]}
              value={fieldFilters}
              onChange={(next) =>
                updateTabFilters({ fieldFilters: next, page: 1 })
              }
              onlyKeys={QUICK_FILTER_KEYS}
              filterVisibility={catalogFilterVisibility}
            />
          </Box>

          <Button
            variant="outlined"
            startIcon={<ListFilter size={16} />}
            onClick={() => setFiltersModalOpen(true)}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 600,
              flexShrink: 0,
              transition: "none",
              borderColor: hasActiveFilters
                ? theme.palette.primary.main
                : theme.palette.divider,
              color: hasActiveFilters
                ? theme.palette.primary.main
                : theme.palette.text.primary,
              backgroundColor: hasActiveFilters
                ? theme.palette.action.hover
                : "transparent",
              "&:hover": {
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            {t("filters-button")}
          </Button>

          <Box sx={{ flex: 1 }} />
          <Box
            sx={{
              width: 220,
              "& > *": {
                minWidth: "0 !important",
                width: "100% !important",
              },
              "& .MuiFormControl-root": {
                minWidth: "0 !important",
                width: "100% !important",
              },
              "& .MuiOutlinedInput-root": { borderRadius: "8px" },
              "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
            }}
          >
            <SelectCst
              id="catalog-sort"
              label={t("sort.most-recent") || ""}
              value={sort ?? ""}
              options={sortOptions}
              onChange={(next: any) => updateTabFilters({ sort: next || null })}
            />
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <BulkActionBarCst
            selectedCount={selectedIds.size}
            checked={allChecked}
            indeterminate={someChecked}
            onToggleAll={handleToggleAll}
            selectedLabel={(count) => t("bulk.selected", { count })}
            actions={[
              {
                key: "enroll",
                label: t("bulk.enroll"),
                icon: <UserPlus size={14} />,
                onClick: () => {},
              },
              {
                key: "associate",
                label: t("bulk.associate-initiative"),
                icon: <Building2 size={14} />,
                onClick: () => {},
              },
              {
                key: "users",
                label: t("bulk.users-groups"),
                icon: <Users size={14} />,
                onClick: () => {},
              },
            ]}
            iconActions={[
              {
                key: "duplicate",
                icon: <Copy size={18} />,
                onClick: () => {},
                tooltip: t("bulk.duplicate") || "",
              },
              {
                key: "edit",
                icon: <Pencil size={18} />,
                onClick: () => {},
                tooltip: t("bulk.edit") || "",
              },
              {
                key: "delete",
                icon: <Trash2 size={18} />,
                onClick: () => {},
                tooltip: t("bulk.delete") || "",
              },
            ]}
          />
        </Box>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : normalizedItems.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 6,
              color: theme.palette.text.tertiary,
            }}
          >
            {t("empty")}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {normalizedItems.map(({ entity, entityType: itemType }) => {
              const segment = segmentForType(itemType);
              return (
                <CatalogItem
                  key={`${itemType}-${entity.id}`}
                  entity={entity}
                  entityType={itemType}
                  selected={selectedIds.has(entity.id)}
                  onSelect={(next) => handleSelectToggle(entity.id, next)}
                  onEnrolled={mutate}
                  onDetails={(e) =>
                    navigate(
                      `/v2/corporate/${corporateId}/detail/catalog/${segment}/${e.id}`,
                    )
                  }
                  onPublish={(e) => {
                    // TODO: inserire api corretta
                    console.log("publish", itemType, e.id);
                  }}
                  onEdit={(e) =>
                    navigate(
                      `/v2/corporate/${corporateId}/detail/catalog/${segment}/${e.id}/edit`,
                    )
                  }
                />
              );
            })}
          </Box>
        )}

        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <PaginatorCst
              page={page}
              pageSize={PAGE_SIZE}
              totalItems={totalItems}
              onPageChange={(next) => updateTabFilters({ page: next })}
            />
          </Box>
        )}

        <FiltersModalCst
          open={filtersModalOpen}
          onClose={() => setFiltersModalOpen(false)}
          pageId={CATALOG_PAGE_ID_BY_ENTITY[entityType]}
          value={fieldFilters}
          onApply={(next) => updateTabFilters({ fieldFilters: next, page: 1 })}
          excludeKeys={modalExcludeKeys}
          filterVisibility={catalogFilterVisibility}
        />
      </Box>
    </AnimationFadeIn>
  );
};

export default CatalogV2;
